/**
 * Renders the supplier's responses for the ReviewQuestionnaire page.
 * Extracted from ReviewQuestionnaire.tsx to keep that file under the
 * 500-line cap (the file was already over before CR-5362). Each
 * question card embeds the per-question reviewer controls.
 */
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Label } from '../../components/ui/label';
import { CheckCircle2, XCircle } from 'lucide-react';
import { formatAnswer } from './reviewQuestionnaireUtils';
import type { Question, Section } from './reviewQuestionnaireUtils';
import type { FilledDatasetValue } from '../../lib/api';
import QuestionReviewPanel from './QuestionReviewPanel';

interface ReviewResponsesSectionProps {
  sections: Section[];
  expandedSections: string[];
  setExpandedSections: (sections: string[]) => void;
  shouldShowSection: (s: Section) => boolean;
  shouldShowQuestion: (q: Question) => boolean;
  getAnswerValue: (questionId: number) => FilledDatasetValue | null | undefined;
  canTakeAction: boolean;
  reload: () => void;
}

export default function ReviewResponsesSection({
  sections,
  expandedSections,
  setExpandedSections,
  shouldShowSection,
  shouldShowQuestion,
  getAnswerValue,
  canTakeAction,
  reload,
}: ReviewResponsesSectionProps) {
  if (!sections || sections.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No questions available in this questionnaire.
      </div>
    );
  }

  return (
    <Accordion type="multiple" value={expandedSections} onValueChange={setExpandedSections} className="space-y-4">
      {sections
        .filter(shouldShowSection)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((section) => (
          <AccordionItem key={section.id} value={`section-${section.id}`} className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex flex-col items-start text-left">
                <span className="font-semibold">{section.title}</span>
                {section.description && <span className="text-sm text-gray-500">{section.description}</span>}
                <span className="text-xs text-gray-400 mt-1">
                  {section.questions?.filter(shouldShowQuestion).length || 0} questions
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 space-y-6">
              {section.questions
                ?.filter(shouldShowQuestion)
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((question) => {
                  const answer = getAnswerValue(question.id);
                  const hasAnswer = answer && (
                    answer.text_value ||
                    answer.number_value !== null ||
                    answer.boolean_value !== null ||
                    answer.date_value ||
                    (answer.choice_values && answer.choice_values.length > 0)
                  );

                  return (
                    <div key={question.id} className={`p-4 rounded-lg border ${hasAnswer ? 'bg-white' : 'bg-gray-50'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <Label className="text-base font-medium">
                          {question.question_text}
                          {question.is_required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        {hasAnswer ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                        ) : (
                          <XCircle className="h-5 w-5 text-gray-300 flex-shrink-0" />
                        )}
                      </div>
                      {question.help_text && <p className="text-sm text-gray-500 mb-2">{question.help_text}</p>}
                      <div className={`mt-2 p-3 rounded ${hasAnswer ? 'bg-blue-50 text-blue-900' : 'bg-gray-100 text-gray-500 italic'}`}>
                        {formatAnswer(question, answer ?? undefined)}
                      </div>
                      {/* CR-5362 Phase 2: per-question reviewer
                          controls. Visible to customer reviewers
                          while the questionnaire is in a review-
                          eligible state; otherwise renders just the
                          prior decision + comment for context. */}
                      <QuestionReviewPanel
                        answer={answer}
                        canReview={canTakeAction}
                        onReviewed={reload}
                      />
                    </div>
                  );
                })}
            </AccordionContent>
          </AccordionItem>
        ))}
    </Accordion>
  );
}
